import { auth, createGoogleAuthProvider } from '../firebase';
import logger from './logger';
import {

  createUserWithEmailAndPassword,

  signInWithPopup,
  signInWithEmailAndPassword } from
  'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { COLLECTIONS } from '../constants/constants';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  guestUsernameToEmail,
  validateGuestUsername,
  isAllowedAuthEmail,
  UNAUTHORIZED_AUTH_EMAIL_MESSAGE
} from './guestAuth';
import { peekRequireCredentialReauth } from './authSession';

const validateEmailDomain = (email) => {
  if (!email) {
    return false;
  }
  const domain = String(email).split('@')[1];
  return Boolean(domain) && domain.toLowerCase() === 'communityforwardsf.org';
};

const mapGoogleSignInError = (error) => {
  if (error?.code === 'auth/popup-closed-by-user') {
    return new Error('Sign-in popup was closed');
  }
  if (error?.code === 'auth/popup-blocked') {
    return new Error('Sign-in popup was blocked by browser');
  }

  const message = String(error?.message || '');
  const details = String(error?.customData?.message || error?.customData?.serverResponse || '');
  if (
    message.includes('communityforwardsf.org') ||
    details.includes('communityforwardsf.org') ||
    (error?.code === 'auth/internal-error' && /PERMISSION_DENIED|permission-denied/.test(`${message} ${details}`))
  ) {
    return new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
  }

  return error;
};

/** @returns {Promise<'created' | 'updated'>} */
export const syncUserProfileOnSignIn = async (user, { authProvider }) => {
  if (!isAllowedAuthEmail(user?.email)) {
    throw new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
  }

  const nameParts = user.displayName?.split(' ') || [];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';
  const userRef = doc(db, COLLECTIONS.USERS, user.uid);
  const existingDoc = await getDoc(userRef);

  if (existingDoc.exists()) {
    await updateDoc(userRef, {
      displayName: user.displayName || existingDoc.data().displayName || '',
      photoURL: user.photoURL || '',
      lastLogin: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      emailVerified: user.emailVerified,
      metadata: {
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime
      }
    });
    return 'updated';
  }

  await setDoc(userRef, {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    firstName,
    lastName,
    photoURL: user.photoURL || '',
    role: 'staff',
    locations: [],
    authProvider,
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp(),
    lastUpdated: serverTimestamp(),
    emailVerified: user.emailVerified,
    disabled: false,
    metadata: {
      creationTime: user.metadata.creationTime,
      lastSignInTime: user.metadata.lastSignInTime
    }
  });
  return 'created';
};

/** Attempt to redeem a pending invitation for the signed-in user. */
export const redeemPendingInvitationIfExists = async (user = auth.currentUser, options = {}) => {
  if (!user?.uid) {
    return { redeemed: false };
  }
  try {
    const redeem = httpsCallable(functions, 'redeemPendingInvitationOnLogin');
    const payload = options.invitationId ? { invitationId: options.invitationId } : {};
    const result = await redeem(payload);
    if (result.data?.redeemed) {
      await user.getIdToken(true);
    }
    return result.data || { redeemed: false };
  } catch (error) {
    logger.warn('redeemPendingInvitationOnLogin failed:', error?.message || error);
    return { redeemed: false, error: error?.message };
  }
};

/** Sync JWT custom claims from the signed-in user's Firestore profile (role + locations). */
export const syncMyClaimsFromProfile = async (user = auth.currentUser) => {
  if (!user?.uid) {
    return { success: false };
  }
  try {
    const syncClaims = httpsCallable(functions, 'syncMyClaimsFromProfile');
    const result = await syncClaims();
    if (result.data?.claimsChanged) {
      await user.getIdToken(true);
    }
    return result.data || { success: false };
  } catch (error) {
    logger.warn('syncMyClaimsFromProfile failed:', error?.message || error);
    return { success: false, error: error?.message };
  }
};

// Process Google sign-in user (domain validation, user document creation, claims)
// Used after popup sign-in to process the user
const processGoogleSignInUser = async (user, options = {}) => {
  try {
    const { skipUserDocumentCreation = false } = options;

    // Domain restriction
    if (!validateEmailDomain(user.email)) {
      await auth.signOut();
      throw new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
    }

    // If skipUserDocumentCreation is true, return early without creating user document
    // This is used during registration when the caller will create the document with invitation data
    if (skipUserDocumentCreation) {
      logger.debug('Skipping user document creation, caller will handle it');
      return user;
    }

    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        const result = await syncUserProfileOnSignIn(user, { authProvider: 'google' });
        logger.debug('Successfully synced user document on Google sign-in:', {
          uid: user.uid,
          email: user.email,
          result
        });
        break;
      } catch (error) {
        logger.error(`Error syncing user document (attempt ${retryCount + 1}):`, error);
        retryCount++;

        if (retryCount === maxRetries) {
          throw new Error(
            `Failed to sync user document after ${maxRetries} attempts: ${error.message}`
          );
        }

        const retryDelay = Math.pow(2, retryCount) * 1000;
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    return user;
  } catch (error) {
    logger.error('Google sign-in processing error:', {
      error: error.message,
      code: error.code,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Ensure user is signed out if there was an error
    try {
      await auth.signOut();
    } catch (signOutError) {
      logger.error('Error signing out after failed sign-in:', signOutError);
    }

    throw error;
  }
};

// Google sign-in with popup (returns user directly)
export const signInWithGoogle = async (options = {}) => {
  try {
    const forceReauth = options.forceReauth ?? peekRequireCredentialReauth();
    const provider = createGoogleAuthProvider({ forceReauth });
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Process the user (domain validation, user document creation, claims)
    await processGoogleSignInUser(user, options);

    return user;
  } catch (error) {
    logger.error('Google sign-in popup error:', {
      error: error.message,
      code: error.code,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    throw mapGoogleSignInError(error);
  }
};

export const createUserWithEmail = async (
  email,
  password,
  firstName,
  lastName,
  role,
  locations = []) => {
  try {
    if (!validateEmailDomain(email)) {
      throw new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
      email,
      firstName,
      lastName,
      role,
      locations,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp()
    });

    return user;
  } catch (error) {
    logger.error('Error creating user:', error);
    throw error;
  }
};

export const signInWithEmailPassword = async (email, password) => {
  try {
    if (!validateEmailDomain(email)) {
      throw new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
    }

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await syncUserProfileOnSignIn(user, { authProvider: 'email' });

    // Sync staff claims and locations from Firestore profile (admin-only setUserClaims is not used here).
    try {
      await syncMyClaimsFromProfile(user);
      logger.debug('Synced claims from profile for user:', user.uid);
    } catch (error) {
      logger.error('Error syncing user claims from profile:', error);
    }

    // Force token refresh to get latest claims
    try {
      await user.getIdToken(true);
      logger.debug('Successfully refreshed user token');
    } catch (error) {
      logger.error('Error refreshing user token:', error);
      // Don't throw here, as the user is already signed in
    }

    return user;
  } catch (error) {
    logger.error('Email/password sign-in error:', error);
    throw error;
  }
};

export const signInGuestWithUsernamePassword = async (username, password) => {
  try {
    const normalizedUsername = validateGuestUsername(username);
    const email = guestUsernameToEmail(normalizedUsername);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    const token = await user.getIdTokenResult(true);
    if (token.claims?.guest !== true) {
      logger.error('Guest sign-in missing guest claims', {
        uid: user.uid,
        email: user.email
      });
      await auth.signOut();
      throw new Error('Guest account is not configured correctly. Contact your administrator.');
    }

    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
        lastLogin: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (firestoreError) {
      logger.warn('Could not update guest lastLogin (non-fatal):', firestoreError);
    }

    return user;
  } catch (error) {
    logger.error('Guest sign-in error:', error);
    if (error.message?.includes('Guest account is not configured')) {
      throw error;
    }
    throw new Error('Invalid username or password');
  }
};

export const SIGN_OUT_TIMEOUT_MS = 5000;
/** Max wait for auth token / claims work before clearing the loading gate. */
export const AUTH_BOOTSTRAP_TIMEOUT_MS = 15000;

export const withTimeout = async (promise, timeoutMs, label = 'operation') => {
  let timeoutId;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`${label} timeout after ${timeoutMs}ms`));
        }, timeoutMs);
      })
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const signOutWithTimeout = async (timeoutMs = SIGN_OUT_TIMEOUT_MS) => {
  await withTimeout(auth.signOut(), timeoutMs, 'signOut');
};

export const signOutUser = async () => {
  try {
    await signOutWithTimeout();
  } catch (error) {
    logger.error('Error signing out:', error);
    throw error;
  }
};

export const createUserWithEmailPassword = async (email, password, userData) => {
  try {
    if (!validateEmailDomain(email)) {
      throw new Error(UNAUTHORIZED_AUTH_EMAIL_MESSAGE);
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user document
    await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
      ...userData,
      uid: user.uid,
      email: user.email,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });

    return user;
  } catch (error) {
    logger.error('User creation error:', error);
    throw error;
  }
};

export const getAuthToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently signed in.');
  }
  // Force refresh if requested to get latest claims
  return user.getIdToken(forceRefresh);
};
